import { Injectable, signal, computed, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom, timer } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { UserInfo } from '../models/user/userInfo';
import { UserSettings } from '../models/user/userSettings';
import { UserNotification } from '../models/user/userNotification';
import { UserService } from './user.service';
import { UserRightsService } from './rollout/user-rights.service';
import { UserRole } from '../enums/userRoleEnum';
import { AuthenticationService } from './authentication.service';
import { TokenRefreshService } from './token-refresh.service';

/**
 * Central state management service for user-related data using Angular signals
 * Replaces scattered user state logic from AppComponent
 */
@Injectable({
  providedIn: 'root'
})
export class UserStateService {
  private userService = inject(UserService);
  private userRightsService = inject(UserRightsService);
  private authService = inject(AuthenticationService);
  private tokenRefreshService = inject(TokenRefreshService);

  // Signals for reactive state management
  readonly userInfo = signal<UserInfo | null>(null);
  readonly userSettings = signal<UserSettings | null>(null);
  readonly userNotifications = signal<UserNotification[]>([]);
  readonly isLoaded = signal<boolean>(false);
  readonly userName = signal<string>('');
  readonly initErrorStatus = signal<number | null>(null);

  // Computed signals
  readonly isInternalUser = computed(() => {
    const user = this.userInfo();
    return user ? user.IsExternal === false : false;
  });

  readonly unreadNotificationsCount = computed(() => {
    return this.userNotifications().filter(n => !n.Readed).length;
  });

  readonly hasDeputy = computed(() => {
    const user = this.userInfo();
    return user?.Deputy?.DeputyActive ?? false;
  });

  readonly deputyFor = computed(() => {
    const user = this.userInfo();
    return user?.DeputyFor ?? [];
  });

  // Legacy Observable support for existing code
  private userInfoSubject = new BehaviorSubject<UserInfo | null>(null);
  readonly userInfo$ = this.userInfoSubject.asObservable();

  constructor() {
    // Start notification polling timer (5 minutes)
    timer(300000, 300000).subscribe(() => {
      if (this.isLoaded()) {
        this.loadUserNotifications();
      }
    });
  }

  /**
   * Initialize user data after authentication
   */
  async initializeUser(): Promise<void> {
    this.initErrorStatus.set(null);

    try {
      const account = this.authService.getActiveAccount();

      if (!account) {
        console.error('No active account found');
        this.initErrorStatus.set(401);
        this.isLoaded.set(true);
        return;
      }

      this.userName.set(account.username ?? account.name ?? '');

      // Load user info
      const userInfo = await firstValueFrom(this.userService.getUserInfo());
      if (!userInfo) {
        console.error('Failed to load user info - no data returned');
        this.isLoaded.set(true);
        return;
      }

      this.userInfo.set(userInfo);
      this.userInfoSubject.next(userInfo);
      this.authService.assignUserInfo(userInfo);
      this.userService.userInfo = userInfo;

      // Initialize user rights
      await this.initializeUserRights(userInfo);

      // Load user project roles
      await this.loadUserProjectRoles(userInfo.Id);

      // Load user settings
      await this.loadUserSettings(userInfo.Id);

      // Load notifications
      await this.loadUserNotifications();

      this.isLoaded.set(true);

    } catch (error) {
      console.error('Failed to initialize user:', error);
      this.initErrorStatus.set(this.extractHttpStatus(error));
      this.isLoaded.set(true); // Still mark as loaded to prevent infinite loading
    }
  }

  needsReauthentication(): boolean {
    return this.initErrorStatus() === 401;
  }

  hasTransientInitializationFailure(): boolean {
    return this.initErrorStatus() === 0;
  }

  private extractHttpStatus(error: unknown): number | null {
    if (error instanceof HttpErrorResponse) {
      return error.status;
    }

    const candidate = error as { status?: unknown };
    return typeof candidate?.status === 'number' ? candidate.status : null;
  }

  /**
   * Initialize user rights from roles
   */
  private async initializeUserRights(userInfo: UserInfo): Promise<void> {
    this.userRightsService.userId = userInfo.Id;
    this.userRightsService.userData = userInfo;

    for (const role of userInfo.Roles) {
      switch (role) {
        case UserRole.Admin:
          this.userRightsService.IsAdmin = true;
          break;
        case UserRole.RO_PP_Poweruser:
          this.userRightsService.RO_PP_Poweruser = true;
          break;
        case UserRole.RO_PP_Worker:
          this.userRightsService.RO_PP_Worker = true;
          break;
        case UserRole.RO_PP_Finance:
          this.userRightsService.RO_PP_Finance = true;
          break;
        case UserRole.EPG_Supervisor:
          this.userRightsService.EPG_Supervisor = true;
          break;
        case UserRole.EPG_Worker:
          this.userRightsService.EPG_Worker = true;
          break;
        case UserRole.EPG_Engineer:
          this.userRightsService.EPG_Engineer = true;
          break;
        case UserRole.EPG_Construction_Manager:
          this.userRightsService.EPG_Construction_Manager = true;
          break;
        case UserRole.EPG_Project_Manager:
          this.userRightsService.EPG_Project_Manager = true;
          break;
        case UserRole.EPG_Finance:
          this.userRightsService.EPG_Finance = true;
          break;
        case UserRole.Partner_User:
          this.userRightsService.Partner_User = true;
          break;
        case UserRole.Supplier_Worker:
          this.userRightsService.Supplier_Worker = true;
          break;
        case UserRole.Supplier_Manager:
          this.userRightsService.Supplier_Manager = true;
          break;
        case UserRole.Supplier_Project_Manager:
          this.userRightsService.Supplier_Project_Manager = true;
          break;
        case UserRole.Supplier_User:
          this.userRightsService.Supplier_User = true;
          break;
      }
    }
  }

  /**
   * Load user project-specific roles
   */
  private async loadUserProjectRoles(userId: number): Promise<void> {
    try {
      const response: any = await firstValueFrom(this.userService.getUserRoles(userId));
      if (response && Array.isArray(response)) {
        for (const projectRole of response) {
          switch (projectRole.RoleName) {
            case UserRole.RO_Project_Manager:
              this.userRightsService.RO_Project_Manager = true;
              break;
            case UserRole.RO_Construction_Manager:
              this.userRightsService.RO_Construction_Manager = true;
              break;
          }
        }
      }
    } catch (error) {
      console.error('Failed to load user project roles:', error);
    }
  }

  /**
   * Load user settings and handle startup page redirection
   */
  private async loadUserSettings(userId: number): Promise<void> {
    try {
      const settings = await firstValueFrom(this.userService.getUserSettings(userId)) as UserSettings;

      if (!settings) {
        // Create default settings
        const defaultSettings = new UserSettings();
        defaultSettings.UserId = userId;
        defaultSettings.DefaultStartPage = null;
        defaultSettings.DefaultTableView = null;
        defaultSettings.MSWindowPosition = null;
        defaultSettings.Language = 'en';

        await firstValueFrom(this.userService.saveUserSettings(userId, defaultSettings));
        this.userSettings.set(defaultSettings);
      } else {
        this.userSettings.set(settings);

        // Store settings in localStorage
        if (settings.DefaultStartPage) {
          localStorage.setItem('startPage', settings.DefaultStartPage);
        }
        if (settings.IndividualPlanningMilestonesJson) {
          localStorage.setItem('planning', settings.IndividualPlanningMilestonesJson);
        }
      }
    } catch (error) {
      console.error('Failed to load user settings:', error);
    }
  }

  /**
   * Load user notifications
   */
  async loadUserNotifications(): Promise<void> {
    try {
      // Try to refresh token silently before fetching notifications
      const tokenRefreshed = await this.tokenRefreshService.tryRefreshTokenSilently();
      if (!tokenRefreshed) {
        // Skip notification polling cycle when silent refresh is not possible.
        // This prevents background polling from forcing interactive auth.
        // Polling will retry in the next 5-minute cycle.
        return;
      }

      const notifications = await firstValueFrom(this.userService.getUserNotifications());
      if (notifications) {
        this.userNotifications.set(notifications);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  }

  /**
   * Mark single notification as read
   */
  async markNotificationAsRead(notification: UserNotification): Promise<void> {
    try {
      notification.Readed = true;
      await firstValueFrom(this.userService.saveUserNotification(notification));
      await this.loadUserNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllNotificationsAsRead(): Promise<void> {
    try {
      await firstValueFrom(this.userService.saveAllUserNotification());
      await this.loadUserNotifications();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }

  /**
   * Update user settings
   */
  async updateUserSettings(settings: UserSettings): Promise<void> {
    try {
      const userId = this.userInfo()?.Id;
      if (!userId) {
        console.error('No user ID available');
      }

      await firstValueFrom(this.userService.saveUserSettings(userId, settings));
      this.userSettings.set(settings);
    } catch (error) {
      console.error('Failed to update user settings:', error);
      throw error;
    }
  }

  /**
   * Update language setting
   */
  async updateLanguage(language: string): Promise<void> {
    const settings = this.userSettings();
    if (settings && this.userInfo()) {
      settings.Language = language;
      await this.updateUserSettings(settings);
      localStorage.setItem('defaultUserLanguage', JSON.stringify(language));
    }
  }

  /**
   * Check if user has a specific role
   */
  hasRole(role: string): boolean {
    const user = this.userInfo();
    return user?.Roles?.includes(role) ?? false;
  }

  /**
   * Reset state (for logout)
   */
  reset(): void {
    this.userInfo.set(null);
    this.userInfoSubject.next(null);
    this.userSettings.set(null);
    this.userNotifications.set([]);
    this.isLoaded.set(false);
    this.userName.set('');

    // Clear localStorage
    localStorage.removeItem('startPage');
    localStorage.removeItem('planning');
  }
}
